import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import SplashScreen from './components/SplashScreen';
import SmoothScroll from './components/SmoothScroll';
import Navbar from './components/Navbar';
import ScrollToTop from './components/ScrollToTop';
import PageTransitionSkeleton from './components/PageTransitionSkeleton';
import NoiseOverlay from './components/NoiseOverlay';
import Hero from './sections/Hero';
import ServiceBar from './sections/ServiceBar';
import VisionMission from './sections/VisionMission';
import Quote from './sections/Quote';
import Newsletter from './sections/Newsletter';
import Footer from './sections/Footer';

import { usePageSEO } from './hooks/usePageSEO';

// Code-split heavy subpages to drastically reduce initial bundle size & parse time
const StaticPage = lazy(() => import('./pages/StaticPage'));
const PastoralTeam = lazy(() => import('./sections/PastoralTeam'));

function HomePage() {
  return (
    <>
      <Hero />
      <VisionMission />
      <Quote />
      <Newsletter />
      <Footer />
    </>
  );
}

function LeadershipPage() {
  React.useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('church:motion-ready'));
    }, 120);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <PastoralTeam showViewAll={false} isPage={true} />
      <Footer />
    </>
  );
}

function AppRoutes() {
  usePageSEO();

  return (
    <SmoothScroll>
      <NoiseOverlay />
      <SplashScreen />
      <ScrollToTop />
      <PageTransitionSkeleton />
      
      <Navbar />
      <main>
        <Suspense fallback={<PageTransitionSkeleton />}>
          <Routes>
            <Route path="/" element={<HomePage />} />

            {/* Church & Leadership */}
            <Route path="/leadership" element={<LeadershipPage />} />
            <Route path="/branches" element={<StaticPage pageKey="branches" />} />
            <Route path="/missionandvision" element={<StaticPage pageKey="missionandvision" />} />
            <Route path="/beliefs" element={<StaticPage pageKey="beliefs" />} />
            <Route path="/bylaws" element={<StaticPage pageKey="bylaws" />} />
            <Route path="/who-is-jesus" element={<StaticPage pageKey="whoIsJesus" />} />

            {/* Ministries */}
            <Route path="/lambs" element={<StaticPage pageKey="lambs" />} />
            <Route path="/young-lion-cubs" element={<StaticPage pageKey="youngLionCubs" />} />
            <Route path="/women" element={<StaticPage pageKey="women" />} />
            <Route path="/council" element={<StaticPage pageKey="council" />} />
            <Route path="/members-area" element={<StaticPage pageKey="membersArea" />} />

            {/* Events, Outreach & Schedule */}
            <Route path="/eventscalendar" element={<StaticPage pageKey="eventscalendar" />} />
            <Route path="/outreach" element={<StaticPage pageKey="outreach" />} />
            <Route path="/missiontrips" element={<StaticPage pageKey="missiontrips" />} />

            {/* Connect & Actions */}
            <Route path="/prayer-request" element={<StaticPage pageKey="prayerRequest" />} />
            <Route path="/new" element={<StaticPage pageKey="new" />} />
            <Route path="/contact" element={<StaticPage pageKey="contact" />} />
            <Route path="/live-services" element={<StaticPage pageKey="liveServices" />} />
            <Route path="/live" element={<StaticPage pageKey="liveServices" />} />

            {/* Blogs & Insights (Coming Soon) */}
            <Route path="/blogs" element={<StaticPage pageKey="blogs" />} />
            <Route path="/blogs/*" element={<StaticPage pageKey="blogs" />} />
            <Route path="/blog" element={<StaticPage pageKey="blogs" />} />
            <Route path="/blog/*" element={<StaticPage pageKey="blogs" />} />

            {/* Legacy Fallbacks */}
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/about" element={<Navigate to="/missionandvision" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
    </SmoothScroll>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
